import React, { useState, useEffect } from 'react';
import { Modal, Table, Tag, Button, message, Popconfirm, Space, Empty, Badge } from 'antd';
import { DeleteOutlined, ClearOutlined, ReloadOutlined } from '@ant-design/icons';
import ObservatoryService from '../services/ObservatoryService';

/**
 * CurrentAlertsCard - Modal for displaying and managing current active alerts
 */
const CurrentAlertsCard = ({ visible, onClose, onAlertsChanged }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible) {
      loadCurrentAlerts();
    }
  }, [visible]);

  const loadCurrentAlerts = async () => {
    try {
      setLoading(true);
      const data = await ObservatoryService.getCurrentAlerts();
      setAlerts(data.alerts || []);
    } catch (error) {
      console.error('Error loading current alerts:', error);
      message.error(`Failed to load current alerts: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClearAlert = async (alertId) => {
    try {
      await ObservatoryService.clearAlert(alertId);
      message.success('Alert cleared successfully');
      loadCurrentAlerts();
      // Notify parent component that alerts changed
      if (onAlertsChanged) {
        onAlertsChanged();
      }

    } catch (error) {
      console.error('Error clearing alert:', error);
      message.error(`Failed to clear alert: ${error.message}`);
    }
  };

  const handleClearAll = async () => {
    try {
      await ObservatoryService.clearAllAlerts();
      message.success('All alerts cleared successfully');
      loadCurrentAlerts();

      // Notify parent component that alerts changed
      if (onAlertsChanged) {
        onAlertsChanged();
      }

    } catch (error) {
      console.error('Error clearing all alerts:', error);
      message.error(`Failed to clear all alerts: ${error.message}`);
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'info': 'blue',
      'warning': 'orange',
      'critical': 'red'
    };
    return colors[severity] || 'default';
  };

  const getComparisonSymbol = (operator) => {
    const symbols = {
      'greater_than': '>',
      'less_than': '<',
      'greater_or_equal': '>=',
      'less_or_equal': '<=',
      'equal_to': '='
    };
    return symbols[operator] || operator;
  };

  const formatValue = (value, unit) => {
    if (unit === 'seconds') {
      return `${(value / 1000).toFixed(2)}s`;
    } else if (unit === 'milliseconds') {
      return `${value.toFixed(0)}ms`;
    } else if (unit === 'dollars') {
      return `$${value.toFixed(4)}`;
    } else if (unit === 'percent') {
      return `${value.toFixed(1)}%`;
    }
    return value.toFixed(2);
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const columns = [
    {
      title: 'Severity',
      dataIndex: 'alert_severity',
      key: 'alert_severity',
      width: 100,
      render: (severity) => (
        <Tag color={getSeverityColor(severity)}>
          {severity?.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Rule',
      dataIndex: 'rule_name',
      key: 'rule_name',
      width: 200,
      ellipsis: true
    },
    {
      title: 'Agent',
      dataIndex: 'agent_name',
      key: 'agent_name',
      width: 180,
      ellipsis: true,
      render: (text) => text || <span style={{ color: '#999' }}>All Agents</span>
    },
    {
      title: 'Type',
      dataIndex: 'agent_type',
      key: 'agent_type',
      width: 120,
      render: (text) => text || <span style={{ color: '#999' }}>All</span>
    },
    {
      title: 'Mode',
      dataIndex: 'execution_context',
      key: 'execution_context',
      width: 120,
      render: (text) => text || <span style={{ color: '#999' }}>All</span>
    },
    {
      title: 'Metric',
      dataIndex: 'metric_name',
      key: 'metric_name',
      width: 150,
      render: (text) => {
        const labels = {
          'avg_llm_latency_ms': 'LLM Latency',
          'avg_trace_latency_ms': 'Trace Latency',
          'total_cost': 'Total Cost',
          'success_rate': 'Success Rate',
          'failed_traces': 'Failed Traces'
        };
        return labels[text] || text;
      }
    },
    {
      title: 'Condition',
      key: 'condition',
      width: 200,
      render: (_, record) => (
        <span>
          <span style={{ fontWeight: 'bold', color: '#f5222d' }}>
            {formatValue(record.metric_value, record.threshold_unit)}
          </span>
          <span style={{ margin: '0 4px', color: '#999' }}>
            {getComparisonSymbol(record.comparison_operator)}
          </span>
          <span style={{ color: '#52c41a' }}>
            {formatValue(record.threshold_value, record.threshold_unit)}
          </span>
        </span>
      )
    },
    {
      title: 'Triggered',
      dataIndex: 'triggered_at',
      key: 'triggered_at',
      width: 140,
      render: (text) => formatDateTime(text)
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        <Popconfirm
          title="Clear this alert?"
          onConfirm={() => handleClearAlert(record.id)}
          okText="Yes"
          cancelText="No"
        >
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            size="small"
          >
            Clear
          </Button>
        </Popconfirm>
      )
    }
  ];

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>
            Current Alerts
            {alerts.length > 0 && (
              <Badge
                count={alerts.length}
                style={{ marginLeft: '12px', backgroundColor: '#f5222d' }}
              />
            )}
          </span>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadCurrentAlerts}
              loading={loading}
              size="small"
            >
              Refresh
            </Button>
            {alerts.length > 0 && (
              <Popconfirm
                title={`Clear all ${alerts.length} alert(s)?`}
                onConfirm={handleClearAll}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  icon={<ClearOutlined />}
                  danger
                  size="small"
                >
                  Clear All
                </Button>
              </Popconfirm>
            )}
          </Space>
        </div>
      }
      visible={visible}
      onCancel={onClose}
      width="90%"
      style={{ top: 20 }}
      footer={null}
      destroyOnClose
    >
      {alerts.length === 0 ? (
        <Empty
          description="No active alerts"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ margin: '40px 0' }}
        />
      ) : (
        <Table
          columns={columns}
          dataSource={alerts}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 1400 }}
          size="small"
        />
      )}
    </Modal>
  );
};

export default CurrentAlertsCard;